import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    IconButton,
} from '@mui/material';
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNotify } from 'react-admin';
import { RAFile } from '@domain/types';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    ResourceCategory,
    uploadResourceWithMetadata,
    type Resource,
} from '@wwf/sdk';

interface MediaUploadDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (resources: Resource[]) => void;
    category: ResourceCategory;
}

export const MediaUploadDialog = ({
    open,
    onClose,
    onSuccess,
    category,
}: MediaUploadDialogProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [metadata, setMetadata] = useState<{
        name: string;
        description: string;
        alt: string;
    }>({
        name: '',
        description: '',
        alt: '',
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const notify = useNotify();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            setError(null);
            setFiles(prev => [...prev, ...acceptedFiles]);

            // Auto-fill name if not set and only one file
            if (!metadata.name && acceptedFiles.length === 1) {
                setMetadata(prev => ({
                    ...prev,
                    name: acceptedFiles[0].name.split('.')[0],
                }));
            }
        },
        [metadata.name]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': [
                '.jpeg',
                '.jpg',
                '.png',
                '.gif',
                '.webp',
                '.avif',
                '.svg',
            ],
            'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.webm'],
            'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
        },
        multiple: true,
    });

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const getFileIcon = (file: File) => {
        if (file.type.startsWith('image/')) return <ImageIcon />;
        if (file.type.startsWith('video/')) return <VideoFileIcon />;
        if (file.type.startsWith('audio/')) return <AudioFileIcon />;
        return <DescriptionIcon />;
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setError('Please select at least one file to upload');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const uploadPromises = files.map(async file => {
                const raFile: RAFile = {
                    src: URL.createObjectURL(file),
                    title: file.name,
                    rawFile: file,
                    type: file.type,
                };

                const resource = await uploadResourceWithMetadata(
                    raFile.rawFile,
                    category,
                    {
                        edition: {
                            name: metadata.name || file.name || null,
                            description: metadata.description || null,
                            alt: metadata.alt || null,
                        },
                    }
                );

                return resource;
            });

            const results = await Promise.all(uploadPromises);
            const successfulUploads = results.filter(
                (r): r is Resource => r != null
            );

            if (successfulUploads.length > 0) {
                notify(
                    `Successfully uploaded ${successfulUploads.length} file(s)`,
                    { type: 'success' }
                );
                if (onSuccess) {
                    onSuccess(successfulUploads);
                }
                handleClose();
            } else {
                setError('Failed to upload files');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(
                err instanceof Error ? err.message : 'Failed to upload files'
            );
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFiles([]);
        setMetadata({ name: '', description: '', alt: '' });
        setError(null);
        setUploading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogCloseButton onClose={handleClose} />
            <DialogTitle>Upload Media</DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    {/* File Drop Zone */}
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive
                                ? 'primary.main'
                                : 'grey.300',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: isDragActive
                                ? 'action.hover'
                                : 'background.paper',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUploadIcon
                            sx={{ fontSize: 48, color: 'grey.400', mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            {isDragActive
                                ? 'Drop files here'
                                : 'Drag & drop files here'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            or click to select files
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block' }}
                        >
                            Supports images, videos, audio files, and documents
                        </Typography>
                    </Box>

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Selected Files */}
                    {files.length > 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Selected Files ({files.length})
                            </Typography>
                            <Stack spacing={1}>
                                {files.map((file, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 2,
                                            border: 1,
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                            backgroundColor: 'background.paper',
                                        }}
                                    >
                                        {getFileIcon(file)}
                                        <Box sx={{ flexGrow: 1, ml: 2 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                            >
                                                {file.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {formatFileSize(file.size)} •{' '}
                                                {file.type}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(index)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Metadata Fields */}
                    {files.length > 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Media Information
                            </Typography>
                            {files.length > 1 && (
                                <Typography
                                    variant="body2"
                                    color="warning.main"
                                    gutterBottom
                                >
                                    Attention, the information will be applied
                                    to all files.
                                </Typography>
                            )}
                            <Stack spacing={2}>
                                <TextField
                                    label="Name"
                                    value={metadata.name}
                                    onChange={e =>
                                        setMetadata(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="Enter a name for the media"
                                    fullWidth
                                />
                                <TextField
                                    label="Description"
                                    value={metadata.description}
                                    onChange={e =>
                                        setMetadata(prev => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                    placeholder="Enter a description (optional)"
                                    multiline
                                    rows={3}
                                    fullWidth
                                />
                                <TextField
                                    label="Alt Text"
                                    value={metadata.alt}
                                    onChange={e =>
                                        setMetadata(prev => ({
                                            ...prev,
                                            alt: e.target.value,
                                        }))
                                    }
                                    placeholder="Enter alt text for accessibility (optional)"
                                    fullWidth
                                />
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={files.length === 0 || uploading}
                    startIcon={
                        uploading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <CloudUploadIcon />
                        )
                    }
                >
                    {uploading
                        ? 'Uploading...'
                        : `Upload ${files.length} File${
                              files.length !== 1 ? 's' : ''
                          }`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
